import { useParams, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useFilters } from "../context/FilterContext";
import { useFetch } from "../lib/useFetch";
import { PageHeader, Panel, Stat, Loading, Chip } from "../components/common/ui";
import { SubTabs } from "../components/common/SubTabs";
import { AreaTrend, Donut, Bars } from "../components/common/Charts";
import { fmt, inr } from "../lib/format";
import { cn } from "../lib/utils";
import { Sparkles, ArrowRight, Clock } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import ConversationHistoryDashboard from "../components/ai/ConversationHistoryDashboard";
import ReportedChatsDashboard from "../components/ai/ReportedChatsDashboard";
import AIErrorLogsDashboard from "../components/ai/AIErrorLogsDashboard";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "history", label: "Conversation History" },
  { key: "reported-chats", label: "Reported Chats" },
  { key: "error-logs", label: "AI Error Log" },
  { key: "customer-care", label: "Customer-Care AI" },
  { key: "retail", label: "Retail AI" },
  { key: "quality", label: "AI Quality" },
  { key: "roi", label: "AI ROI" },
];

const OPERATIONS_TABS = [
  { key: "history", label: "Conversation History" },
  { key: "reported-chats", label: "Reported Chats" },
  { key: "error-logs", label: "AI Error Log" },
];

export default function AIControlCenter() {
  const { tab = "overview" } = useParams();
  const { user } = useAuth();
  const { range } = useFilters();

  const isOperations = user?.role === "operations";
  const tabsList = isOperations ? OPERATIONS_TABS : TABS;

  // If operations user tries to access non-allowed tabs, redirect to history
  if (isOperations && tab !== "history" && tab !== "reported-chats" && tab !== "error-logs") {
    return <Navigate to="/ai/history" replace />;
  }

  return (
    <div data-testid="ai-page">
      <PageHeader
        title={isOperations ? "Customer Conversations & Reports" : "AI Control Center"}
        subtitle={isOperations ? "Review and monitor conversation history, reported chats, and runtime error logs." : "How your AI agents are helping the business — usage, quality and ROI."}
      />
      <SubTabs base="/ai" tab={tab} items={tabsList} />
      {tab === "overview" && !isOperations && <Overview range={range} />}
      {tab === "history" && <ConversationHistoryDashboard />}
      {tab === "reported-chats" && <ReportedChatsDashboard />}
      {tab === "error-logs" && <AIErrorLogsDashboard />}
      {tab === "customer-care" && !isOperations && <AgentView url={`/ai/customer-care?range=${range}`} dep={range} />}
      {tab === "retail" && !isOperations && <AgentView url={`/ai/retail?range=${range}`} dep={range} retail />}
      {tab === "quality" && !isOperations && <AIQuality />}
      {tab === "roi" && !isOperations && <ROI range={range} />}
    </div>
  );
}


function Overview({ range }) {
  const { data, loading } = useFetch(`/ai/overview?range=${range}`, [range]);
  if (loading || !data) return <Loading rows={4} />;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {data.metrics.map((m) => (
          <div key={m.label} className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="text-xs text-slate-500">{m.label}</div>
            <div className="kpi-num text-xl font-extrabold text-slate-900 mt-1">{fmt(m.value, m.format)}</div>
          </div>
        ))}
      </div>
      <Panel title="AI Assisted Interactions Trend"><AreaTrend data={data.trend} color="#88D317" height={280} /></Panel>
    </div>
  );
}

function AgentView({ url, dep, retail }) {
  const { data, loading } = useFetch(url, [dep]);
  if (loading || !data) return <Loading rows={4} />;
  const tiles = [
    ["Total Tickets", data.tickets], ["AI Assisted", data.assisted], ["Generated", data.generated],
    ["Accepted", data.accepted], ["Edited", data.edited], ["Rejected", data.rejected],
    ["Escalated", data.escalated], ["KB Retrievals", data.kb_retrievals],
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {tiles.map(([l, v]) => <Stat key={l} label={l} value={Number(v).toLocaleString("en-IN")} />)}
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <Panel title="Before vs After AI" className="lg:col-span-1">
          <div className="space-y-4 text-center">
            <div>
              <div className="text-xs text-slate-400">Without AI</div>
              <div className="kpi-num text-2xl font-extrabold text-slate-400">{data.handling_without}m</div>
            </div>
            <div>
              <div className="text-xs text-slate-400">With AI</div>
              <div className="kpi-num text-3xl font-extrabold text-primary">{data.handling_with}m</div>
            </div>
            <div className="bg-secondary/10 rounded-lg py-2">
              <div className="text-xs text-emerald-700">Time saved / ticket</div>
              <div className="kpi-num text-xl font-extrabold text-emerald-600">{(data.handling_without - data.handling_with).toFixed(1)}m</div>
            </div>
          </div>
        </Panel>
        <Panel title="Impact Summary" className="lg:col-span-2">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Total Time Saved" value={`${data.time_saved_hours.toLocaleString("en-IN")} h`} tone="text-primary" />
            <Stat label="Estimated Labour Value" value={"₹" + inr(data.labour_value)} tone="text-emerald-600" />
            <Stat label="CSAT" value={data.csat} />
            <Stat label="AI Cost" value={"₹" + inr(data.cost)} />
          </div>
          <div className="mt-4"><AreaTrend data={data.trend} color="#6E3667" height={160} /></div>
        </Panel>
      </div>
      {retail && data.sessions && (
        <Panel title="Retail AI Sessions" subtitle="Hub → Employee → AI Session">
          <div className="max-h-80 overflow-y-auto scrollbar-thin">
            <Table>
              <TableHeader><TableRow><TableHead>Hub</TableHead><TableHead>Employee</TableHead><TableHead>Ticket</TableHead><TableHead>Query</TableHead><TableHead className="text-right">Confidence</TableHead><TableHead>Outcome</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.sessions.map((s) => (
                  <TableRow key={s.id} className="hover:bg-slate-50">
                    <TableCell className="text-slate-500">{s.hub_name}</TableCell>
                    <TableCell className="font-medium">{s.employee}</TableCell>
                    <TableCell className="text-slate-500">{s.ticket}</TableCell>
                    <TableCell className="text-slate-500">{s.query}</TableCell>
                    <TableCell className="text-right">{s.confidence}%</TableCell>
                    <TableCell><Chip className="bg-slate-100 text-slate-600">{s.outcome}</Chip></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Panel>
      )}
    </div>
  );
}

function AIQuality() {
  const { data, loading } = useFetch("/ai/quality", []);
  if (loading || !data) return <Loading rows={4} />;
  return (
    <div className="space-y-6">
      <Panel title="AI Improvement Opportunities" subtitle="Where AI is failing and how to fix it">
        <div className="space-y-3">
          {data.opportunities.map((o, i) => (
            <div key={i} className="flex gap-3 border-l-2 border-secondary bg-secondary/5 rounded p-3">
              <Sparkles className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-slate-800">{o.title}</div>
                <p className="text-sm text-slate-600 mt-0.5">{o.detail}</p>
              </div>
              <Chip className={cn("ml-auto shrink-0 h-fit", o.impact === "high" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700")}>{o.impact}</Chip>
            </div>
          ))}
        </div>
      </Panel>
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Response Outcomes"><Donut data={data.breakdown} height={280} /></Panel>
        <Panel title="Low-Confidence Answers">
          <div className="space-y-2 max-h-[280px] overflow-y-auto scrollbar-thin">
            {data.low_confidence.map((s) => (
              <div key={s.id} className="flex items-center justify-between text-sm border border-slate-100 rounded-md px-3 py-2">
                <span className="text-slate-600">{s.query}<span className="text-xs text-slate-400 ml-2">{s.agent}</span></span>
                <span className="font-semibold text-amber-600">{s.confidence}%</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function ROI({ range }) {
  const { data, loading } = useFetch(`/ai/roi?range=${range}`, [range]);
  if (loading || !data) return <Loading rows={4} />;
  return (
    <div className="space-y-6">
      <div className="bg-primary text-white rounded-lg p-6 flex items-center justify-between">
        <div>
          <div className="text-white/70 text-sm">AI Business Impact — Estimated Value / AI Cost</div>
          <div className="kpi-num text-4xl font-extrabold mt-1">{data.roi}x ROI</div>
        </div>
        <div className="text-right text-sm text-white/80">
          <div>Business Value: ₹{inr(data.business_value)}</div>
          <div>AI Cost: ₹{inr(data.ai_cost)}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Stat label="Time Saved" value={`${data.time_saved_hours.toLocaleString("en-IN")} h`} />
        <Stat label="Labour Savings" value={"₹" + inr(data.labour_savings)} tone="text-emerald-600" />
        <Stat label="Tickets Automated" value={data.tickets_automated.toLocaleString("en-IN")} />
        <Stat label="Productivity ↑" value={`${data.productivity_gain}%`} />
        <Stat label="Resolution ↑" value={`${data.resolution_gain}%`} />
        <Stat label="Escalation ↓" value={`${data.escalation_reduction}%`} />
        <Stat label="CX ↑" value={`${data.cx_improvement}%`} />
      </div>
      <Panel title="ROI by Agent">
        <Table>
          <TableHeader><TableRow><TableHead>Agent</TableHead><TableHead className="text-right">Cost</TableHead><TableHead className="text-right">Value</TableHead><TableHead className="text-right">ROI</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.by_agent.map((a) => (
              <TableRow key={a.agent} className="hover:bg-slate-50">
                <TableCell className="font-medium">{a.agent}</TableCell>
                <TableCell className="text-right">₹{inr(a.cost)}</TableCell>
                <TableCell className="text-right">₹{inr(a.value)}</TableCell>
                <TableCell className="text-right font-semibold text-primary">{a.roi}x</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>
    </div>
  );
}
